import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import shallow from 'zustand/shallow';

import useStore from '@/stores/useStore';
import useLoaderStore from '@/stores/useLoaderStore';
import useBrandStore from '@/stores/useBrandStore';

import useSetProperties from '@/helpers/useSetProperties';

import { Loader, Link } from './components/blocks';
import { Header, Footer, Main } from './components/containers';

import { LandingPage } from '@/pages';

import { ReactComponent as YoutubeIcon } from '@/assets/svg/icons/youtube.svg';
import { ReactComponent as InstagramIcon } from '@/assets/svg/icons/instagram.svg';
import { ReactComponent as LinkedInIcon } from '@/assets/svg/icons/linkedin.svg';
import { ReactComponent as FacebookIcon } from '@/assets/svg/icons/facebook.svg';
import { ReactComponent as TwitterIcon } from '@/assets/svg/icons/twitter.svg';

function App() {
    const rootContainer = useRef<HTMLDivElement>(null);

    const getDetails = useRef(useStore((state) => state.getDetails));
    const { i18n } = useTranslation();

    useEffect(() => {
        getDetails.current?.(i18n.language);
    }, [i18n.language]);

    const data = useStore((state) => state.data);
    const loading = useLoaderStore((state) => state.loading);

    const saveCompany = useRef(useBrandStore((state) => state.saveCompanyName));
    const { companyName, themes } = useBrandStore(
        (state) => ({
            companyName: state.companyName,
            themes: state.themes,
        }),
        shallow
    );

    useEffect(() => {
        if (data.brand) {
            saveCompany.current?.(data.brand);
        }
    }, [data]);

    /* Using custom properties to change the theme */
    const brand = themes[companyName!];
    useSetProperties(brand, rootContainer);

    const socialMediaIcons: any = {
        instagram: <InstagramIcon />,
        facebook: <FacebookIcon />,
        twitter: <TwitterIcon />,
        linkedIn: <LinkedInIcon />,
        youtube: <YoutubeIcon />,
    };

    const socialMediaLinks: any = {
        comexi: {
            facebook: 'https://www.facebook.com/ComexiGroup',
            twitter: 'https://twitter.com/comexigroup',
            linkedIn: 'https://www.linkedin.com/company/comexigroup',
            youtube: 'https://www.youtube.com/user/ComexiGroup',
        },
        reifenhauser: {
            instagram: 'https://www.instagram.com/reifenhausergroup/',
            twitter: 'https://twitter.com/extrusioneers',
            linkedIn: 'https://www.linkedin.com/company/reifenhausergroup/',
            youtube: 'https://www.youtube.com/c/ReifenhauserGroup',
        },
        rcycle: {
            twitter: 'https://twitter.com/rcycle5',
            linkedIn: 'https://www.linkedin.com/company/r-cycle',
        },
        exxonmobil: {
            twitter: 'https://www.exxonmobil.be/nl-BE/News/Newsroom',
            linkedIn: 'https://www.linkedin.com/company/exxonmobil/',
            youtube: 'https://www.youtube.com/user/exxonmobil',
        },
    };

    return (
        <div ref={rootContainer} id={'root__container'}>
            <Header ariaLabel={'Banner'} company={companyName} />

            <Loader />

            <Main ariaLabel={'Main Content'}>
                {!loading ? <LandingPage /> : null}
            </Main>

            <Footer ariaLabel={'Footer'}>
                {companyName
                    ? Object.getOwnPropertyNames(
                          socialMediaLinks[companyName!]
                      ).map((socialMediaKey: string, idx: number) => (
                          <Link
                              key={idx}
                              href={
                                  socialMediaLinks[companyName!][socialMediaKey]
                              }
                          >
                              {socialMediaIcons[socialMediaKey]}
                          </Link>
                      ))
                    : null}
            </Footer>
        </div>
    );
}

export default App;
